// Pure extraction from src/lib/agent/story-voice.ts. No legacy serializer, provider or client imports.
import type {Word as VoiceWord} from "../../protocol/transcription-words";
type VoiceLineTiming={text:string;startSec:number;endSec:number};
const hasSpeakableText=(s:string)=>/[\p{L}\p{N}]/u.test(s);
const SILENCE_OPTS={leadSec:.08,tailSec:.15};
export const SPEECH_ONSET_ALIGN_MAX_CELLS=4_000_000;
export type GapSealLevel = "line" | "mid" | "max";
/** 강도별 캡(초) — 하: 줄 사이 0.17/단어 ∞ · 중: 0.17/0.2 · 강: 0.1/0.1 (회장님 2026-07-07: 강 0.08→0.1 완화). */
export const GAP_SEAL_LEVEL_CAPS: Record<GapSealLevel, { lineGapCapSec: number; wordGapCapSec: number }> = {
  line: { lineGapCapSec: 0.17, wordGapCapSec: Infinity },
  mid: { lineGapCapSec: 0.17, wordGapCapSec: 0.2 },
  max: { lineGapCapSec: 0.1, wordGapCapSec: 0.1 },
};

const QUIET_SUSTAIN_SEC = 0.03; // 이 길이만큼 연속 조용해야 '무음'으로 인정(파형 잔떨림 무시)

/** 🎚 트랙 적응형 무음 임계(회장님 2026-07-07 실사용 제보의 근본 수정) — 절대 임계(0.008)는 TTS
 *  코덱 노이즈/배경 잡음이 그보다 큰 트랙에서 '지속 무음'을 영영 못 찾아 컷이 0에 수렴했다(48초에 2곳).
 *  20ms RMS 창의 하위 15퍼센타일 = 노이즈 플로어로 보고, 그 3배(+절대 하한/상한 클램프)를 임계로 쓴다.
 *  → 잡음 낀 트랙에서도 "그 트랙 기준의 조용함"이 무음으로 잡힌다. */
export function estimateQuietThresh(samples: Float32Array, sr: number): number {
  const win = Math.max(1, Math.floor(0.02 * sr));
  const n = Math.floor(samples.length / win);
  if (n < 8) return 0.008;
  const rms: number[] = [];
  for (let b = 0; b < n; b++) {
    let acc = 0;
    const from = b * win;
    for (let i = 0; i < win; i++) acc += samples[from + i] * samples[from + i];
    rms.push(Math.sqrt(acc / win));
  }
  rms.sort((a, b) => a - b);
  const floor = rms[Math.floor(n * 0.15)] ?? 0;
  return Math.min(0.06, Math.max(0.006, floor * 3));
}

/** fromSec부터 앞으로 훑어 '지속 무음'이 시작되는 시각. 없으면 boundSec. (발화 꼬리 실측) */
export function scanQuietForward(samples: Float32Array, sr: number, fromSec: number, boundSec: number, thresh = 0.008): number {
  const win = Math.max(1, Math.floor(QUIET_SUSTAIN_SEC * sr));
  const from = Math.max(0, Math.floor(fromSec * sr));
  const to = Math.min(samples.length, Math.ceil(boundSec * sr));
  let quiet = 0;
  for (let i = from; i < to; i++) {
    if (Math.abs(samples[i]) < thresh) {
      quiet++;
      if (quiet >= win) return (i - win + 1) / sr;
    } else quiet = 0;
  }
  return boundSec;
}

/** fromSec에서 뒤로 훑어 '지속 무음'이 끝나는(발화 직전) 시각. 없으면 boundSec. (온셋 실측) */
export function scanQuietBackward(samples: Float32Array, sr: number, fromSec: number, boundSec: number, thresh = 0.008): number {
  const win = Math.max(1, Math.floor(QUIET_SUSTAIN_SEC * sr));
  const from = Math.min(samples.length - 1, Math.ceil(fromSec * sr));
  const to = Math.max(0, Math.floor(boundSec * sr));
  let quiet = 0;
  for (let i = from; i >= to; i--) {
    if (Math.abs(samples[i]) < thresh) {
      quiet++;
      if (quiet >= win) return (i + win - 1) / sr;
    } else quiet = 0;
  }
  return boundSec;
}

/** 🔇 에너지 스냅 컷 계산(레벨·여유 공통 엔진) — "잘라낼 무음 구간"만 반환(발화 불가침).
 *  줄 경계 gap 은 lineGapCapSec, 줄 안 단어 사이는 wordGapCapSec 까지만 남긴다(∞=안 자름).
 *  각 gap: [ASR 단어끝→파형 무음 시작(qs)] · [파형 무음 끝(qe)→ASR 다음 단어 시작] 은 발화로 보호,
 *  실측 무음(qs..qe)이 cap+2·margin 을 넘을 때만 그 한복판을 잘라 cap+2·margin 만 남긴다. */
export function computeRefinedCuts(
  samples: Float32Array,
  sr: number,
  words: VoiceWord[],
  lineEndWordIdx: Set<number>,
  opts: { lineGapCapSec: number; wordGapCapSec: number; marginSec?: number },
): Array<[number, number]> {
  const cuts: Array<[number, number]> = [];
  if (!words.length || !samples.length) return cuts;
  const totalSec = samples.length / sr;
  const margin = Math.max(0, opts.marginSec ?? 0);
  const thresh = estimateQuietThresh(samples, sr); // 트랙 적응형(절대 임계의 컷 0 수렴 수정)
  const add = (a: number, b: number) => {
    if (b - a > 0.005) cuts.push([a, b]);
  };
  // 머리 — 첫 발화 전 실측 무음은 lead+margin 만 남기고 컷.
  {
    const qe = scanQuietBackward(samples, sr, words[0].start, 0, thresh);
    add(0, qe - (SILENCE_OPTS.leadSec + margin));
  }
  for (let i = 0; i < words.length - 1; i++) {
    const cap = lineEndWordIdx.has(i) ? opts.lineGapCapSec : opts.wordGapCapSec;
    if (!Number.isFinite(cap)) continue; // ∞ — 이 gap 은 손대지 않음
    // ★온셋(start) 앵커 실측(회장님 2026-07-09 "강으로 해도 동일"의 근본): 종전은 단어 end→다음 start
    //   의 '보고된 gap'을 게이트(s-e<=0 이면 continue)로 삼았는데, ASR(Soniox)이 단어 end 를 다음
    //   온셋까지 늘려 보고(greedy)하는 일이 잦아 gap 이 0 이하로 접히면 ★실제 귀에 들리는 무음이 통째로
    //   스킵★됐다(어떤 강도에서도 안 잘림). 신뢰 낮은 end 대신, 신뢰 높은 다음 온셋(start)에서 ★뒤로★
    //   훑어 '다음 온셋 직전의 연속 무음' 한 덩어리만 실측한다 — 발화가 다시 나오면 멈추므로 단어 내부
    //   음절 무음(중간에 발화가 끼어 run 이 끊김)에는 오검출되지 않는다(Codex P1: onset 에서 앞으로 훑으면
    //   내부 음절 무음을 gap 시작으로 잡아 발화를 자를 위험). cap+margin 미만은 안 자름(미세무음 보존).
    // ★sustain 판정(2026-07-13 회귀 수리): 온셋 앞 잔여 발화(숨·여린 꼬리)를 샘플 단위로 스킵하면
    //   파형 제로크로싱(진폭이 순간 임계 아래인 1~3샘플)에 걸려 무음 run 이 잔떨림으로 오판돼 gap
    //   전체가 스킵됐다 — scanQuietBackward 의 '0.03s 연속 조용' 카운터로 지속 무음 끝(qe)을 찾는다.
    const nextOnset = words[i + 1].start;
    const prevStart = words[i].start;
    const qe = scanQuietBackward(samples, sr, nextOnset, prevStart, thresh);
    if (qe <= prevStart) continue; // 지속 무음 없음(연속 발화) — 스킵
    // run 시작(qs): 확정 무음에서 뒤로 확장, 발화(임계 이상)를 만나면 정지 — 앞 단어 꼬리 불가침.
    const lo = Math.max(0, Math.floor(prevStart * sr));
    let j = Math.min(samples.length - 1, Math.ceil(qe * sr) - 1);
    while (j >= lo && Math.abs(samples[j]) < thresh) j--;
    const qs = (j + 1) / sr;
    const keep = cap + margin * 2; // 남길 무음 총량(경계별 margin 포함)
    if (qe - qs > keep) add(qs + keep / 2, qe - keep / 2);
  }
  // 꼬리 — 마지막 발화 뒤 실측 무음은 tail+margin 만 남기고 컷.
  //   ★온셋 앵커(2026-07-16 잔여 봉합): 종전은 신뢰 낮은 마지막 word.end 에서 앞으로 스캔 — ASR 이
  //   마지막 단어 end 를 파일 끝까지 늘려 보고(greedy)하면 스캔 시작점이 끝에 붙어 꼬리 무음이 통째로
  //   안 잘렸다. 인터워드와 동일하게 파일 끝에서 ★뒤로★ 지속 무음(sustain)을 실측하고, run 시작(qs)은
  //   확정 무음에서 뒤로 확장(발화·여린 소리를 만나면 정지 — 마지막 발화 꼬리 불가침).
  {
    const lastStart = words[words.length - 1].start;
    const qe = scanQuietBackward(samples, sr, totalSec, lastStart, thresh); // 끝쪽 지속 무음 존재 확인(없으면 lastStart)
    // ★접미(suffix) 무음 가드(Codex MAJOR 2026-07-16): scanQuietBackward 는 "끝에서 뒤로 가다 처음
    //   만나는 지속 무음"을 반환할 뿐 ★파일 끝 자체가 무음★이란 보장이 아니다 — 발화→긴 무음→outro
    //   (BGM·잔향이 끝까지)면 중간 무음을 잡아 outro 를 통째로 잘랐다. 끝이 무음일 때만(qe 가 파일 끝에
    //   붙어 반환될 때만) 꼬리 컷. outro 뒤 끝 무음이 따로 있으면 raw 확장이 outro 에서 멈춰 그 뒤만 잘린다.
    const endIsQuiet = qe >= totalSec - QUIET_SUSTAIN_SEC * 2;
    if (qe > lastStart && endIsQuiet) {
      const lo = Math.max(0, Math.floor(lastStart * sr));
      let j = Math.min(samples.length - 1, Math.ceil(qe * sr) - 1);
      while (j >= lo && Math.abs(samples[j]) < thresh) j--;
      const qs = (j + 1) / sr;
      add(qs + SILENCE_OPTS.tailSec + margin, totalSec);
    }
  }
  return cuts.filter(([a, b]) => b > a && a >= 0 && b <= totalSec).sort((x, y) => x[0] - y[0]);
}


export function shiftWordsByCuts(words: VoiceWord[], cuts: Array<[number, number]>): VoiceWord[] {
  const removedBefore = (t: number) => {
    let r = 0;
    for (const [a, b] of cuts) {
      if (b <= t) r += b - a;
      else if (a < t) r += t - a;
    }
    return r;
  };
  return words.map((w) => ({ text: w.text, start: w.start - removedBefore(w.start), end: w.end - removedBefore(w.end) }));
}


export function lineEndWordIndices(lines: string[], words: VoiceWord[]): Set<number> {
  const norm = normalizeForLineTiming;
  const out = new Set<number>();
  let wi = 0;
  for (let i = 0; i < lines.length; i++) {
    if (!hasSpeakableText(lines[i] ?? "")) continue; // 소리를 차지하지 않는 줄 = 단어를 소비하지 않는다
    const need = Math.max(1, norm(lines[i]).length);
    let consumed = 0;
    while (wi < words.length && consumed < need) {
      consumed += norm(words[wi].text).length;
      wi++;
    }
    if (wi > 0) out.add(wi - 1);
    if (wi >= words.length) break;
  }
  return out;
}


const normalizeForLineTiming = (s: string) =>
  (s || "")
    .normalize("NFC")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/[^0-9A-Za-z가-힯ㄱ-ㆎ]/g, "");


export type LineTimingAlignment = {
  timings: VoiceLineTiming[];
  /** ASR 단어 소비로 확정된 줄 수(0 = 전 줄 추정). */
  measured: number;
  /** 대상 줄 수(= lines.length). */
  total: number;
};


export function alignLineOnsetsToSpeech(lines: string[], words: VoiceWord[]): LineTimingAlignment {
  const norm = normalizeForLineTiming;
  // ① 실발화 글자열 — 토큰 구간을 글자 수로 안분해 글자마다 [시작, 끝] 을 준다.
  const tChars: string[] = [];
  const tStart: number[] = [];
  const tEnd: number[] = [];
  for (const w of words) {
    const s = Array.from(norm(String(w?.text ?? "")));
    if (!s.length) continue;
    const st = Math.max(0, Number(w.start) || 0);
    const en = Math.max(st, Number(w.end) || st);
    for (let k = 0; k < s.length; k++) {
      tChars.push(s[k]);
      tStart.push(st + ((en - st) * k) / s.length);
      tEnd.push(st + ((en - st) * (k + 1)) / s.length);
    }
  }
  // ② 대본 글자열 — 글자마다 소속 줄 번호.
  const sChars: string[] = [];
  const sLine: number[] = [];
  lines.forEach((l, i) => {
    for (const c of Array.from(norm(l))) { sChars.push(c); sLine.push(i); }
  });
  const n = sChars.length;
  const m = tChars.length;
  // 계산량 상한 — 넘으면 종전 방식(정렬 품질보다 화면이 멎지 않는 쪽).
  if (!n || !m || (n + 1) * (m + 1) > SPEECH_ONSET_ALIGN_MAX_CELLS) return alignLineTimingsFromWords(lines, words);

  // ③ 준전역 편집거리 DP — D[i][j] = S[0..i) 전부를 T[0..j) 에 정렬한 최소 비용. 실발화 앞 군말은 공짜(D[0][j]=0).
  const W = m + 1;
  const D = new Int32Array((n + 1) * W);
  for (let i = 1; i <= n; i++) D[i * W] = i;
  for (let i = 1; i <= n; i++) {
    const si = sChars[i - 1];
    for (let j = 1; j <= m; j++) {
      const diag = D[(i - 1) * W + (j - 1)] + (si === tChars[j - 1] ? 0 : 1);
      const del = D[(i - 1) * W + j] + 1; // 대본 글자를 말하지 않음
      const ins = D[i * W + (j - 1)] + 1; // 대본에 없는 말
      D[i * W + j] = diag <= del ? (diag <= ins ? diag : ins) : (del <= ins ? del : ins);
    }
  }
  // 실발화 뒤 군말도 공짜 — 마지막 행의 최소점에서 역추적.
  let jEnd = 0;
  for (let j = 1; j <= m; j++) if (D[n * W + j] < D[n * W + jEnd]) jEnd = j;
  const pos = new Int32Array(n).fill(-1); // 대본 글자 → 짝지어진 실발화 글자(일치·치환)
  const exact = new Uint8Array(n); // 정확히 일치했는가
  {
    // ★동점이면 짝짓기(대각선)를 ★마지막★ 에 고른다 — 우선순위 «실발화 건너뛰기 → 대본 글자 버리기 → 짝짓기»★.
    //   뒤에서 거꾸로 걸을 때 대각선을 먼저 고르면, 같은 글자(「…요」「…다고요」)가 여럿인 곳에서 ★늦은 쪽이
    //   이른 쪽 글자를 가로챈다★. 실측 두 형상: ①모델이 한 줄을 건너뛰자 그 줄 끝 「요」가 앞 줄 끝 「요」를
    //   먹어 앞 줄 끝이 0.12초 당겨지고 건너뛴 줄이 앞 줄 말 도중에 놓였다(«대본 버리기»와 동점) ②덧붙인 문장이
    //   「…다고요」로 끝나자 L0 끝이 덧붙인 말 끝으로 끌려갔다(«말 건너뛰기»와 동점).
    //   세 선택은 비용이 같을 때만 갈리므로 정렬 비용(최적성)은 불변이고, 완전 일치 구간은 대각선만 유효하다.
    let i = n;
    let j = jEnd;
    while (i > 0 && j > 0) {
      const here = D[i * W + j];
      const same = sChars[i - 1] === tChars[j - 1];
      if (here === D[i * W + (j - 1)] + 1) {
        j--;
      } else if (here === D[(i - 1) * W + j] + 1) {
        i--;
      } else {
        pos[i - 1] = j - 1;
        exact[i - 1] = same ? 1 : 0;
        i--; j--;
      }
    }
  }

  // ④⑤ 줄별 확정 시각.
  type Hit = { startSec: number; endSec: number; firstPos: number; lastPos: number };
  const hits: Array<Hit | null> = new Array(lines.length).fill(null);
  {
    let p = 0;
    for (let li = 0; li < lines.length; li++) {
      const lineStart = p;
      let exactCount = 0;
      while (p < n && sLine[p] === li) {
        if (exact[p]) exactCount++;
        p++;
      }
      const len = p - lineStart;
      if (len === 0 || exactCount < Math.max(1, Math.ceil(len * 0.5))) continue;
      // ★시작·끝은 «두 글자 이상 이어 붙은 일치»에서만 잡는다★ — 동점 정렬이 흩뿌린 외톨이 일치 한 글자
      //   (「요」「다」 같은 흔한 글자)가 줄 시작을 이르게, 끝을 늦게 끌지 못하게 한다. 한 글자짜리 줄만 예외.
      const runAt = (q: number) => q + 1 < p && exact[q] && exact[q + 1] && pos[q] >= 0 && pos[q + 1] === pos[q] + 1;
      let firstQ = -1;
      for (let q = lineStart; q < p; q++) if (runAt(q)) { firstQ = q; break; }
      let lastQ = -1;
      for (let q = p - 2; q >= lineStart; q--) if (runAt(q)) { lastQ = q + 1; break; }
      if (firstQ < 0 || lastQ < 0) {
        if (len !== 1 || pos[lineStart] < 0) continue;
        firstQ = lastQ = lineStart;
      }
      // 줄 머리 몇 글자가 치환(다듬은 말)으로 짝지어졌으면 그 글자까지 거슬러 올라간다 — 첫 일치보다 먼저 말했다.
      let headQ = firstQ;
      while (headQ - 1 >= lineStart && pos[headQ - 1] >= 0 && pos[headQ - 1] === pos[headQ] - 1) headQ--;
      const firstPos = pos[headQ];
      const lastPos = pos[lastQ];
      hits[li] = { startSec: tStart[firstPos], endSec: tEnd[lastPos], firstPos, lastPos };
    }
  }
  const measured = hits.reduce((acc, h) => acc + (h ? 1 : 0), 0);
  const totalSec = tEnd.length ? tEnd[tEnd.length - 1] : 0;
  if (measured === 0) {
    const per = Math.max(0.1, totalSec) / Math.max(1, lines.length);
    return { timings: lines.map((text, j) => ({ text, startSec: per * j, endSec: per * (j + 1) })), measured: 0, total: lines.length };
  }

  // ⑤ 미확정 줄 — 연속 구간을 «앞 확정 줄 다음 실발화 글자 ~ 뒤 확정 줄 첫 글자» 사이에 배치.
  const out: VoiceLineTiming[] = [];
  for (let i = 0; i < lines.length; i++) {
    const hit = hits[i];
    if (hit) { out.push({ text: lines[i], startSec: hit.startSec, endSec: hit.endSec }); continue; }
    let end = i;
    while (end + 1 < lines.length && !hits[end + 1]) end++;
    const prev = i > 0 ? hits[i - 1] : null;
    const next = end + 1 < lines.length ? hits[end + 1] : null;
    // 구간 시작 = 앞 줄 다음으로 «말이 다시 시작하는» 글자(없으면 앞 줄 끝 · 맨 앞이면 첫 실발화).
    const gapFirstPos = prev ? prev.lastPos + 1 : 0;
    const gapLastPos = next ? next.firstPos - 1 : m - 1;
    const left = gapFirstPos <= gapLastPos && gapFirstPos < m
      ? tStart[gapFirstPos]
      : (prev ? prev.endSec : (next ? next.startSec : 0));
    const right = Math.max(left, next ? next.startSec : totalSec);
    const count = end - i + 1;
    const per = (right - left) / count;
    for (let j = i; j <= end; j++) {
      out.push({ text: lines[j], startSec: left + per * (j - i), endSec: left + per * (j - i + 1) });
    }
    i = end;
  }
  // 단조 보장(정렬 경로는 이미 단조지만, 보간 경계가 맞닿을 때 역전 0 을 계약으로 잠근다).
  let floor = 0;
  for (const t of out) {
    t.startSec = Math.max(floor, t.startSec);
    t.endSec = Math.max(t.startSec, t.endSec);
    floor = t.startSec;
  }
  return { timings: out, measured, total: lines.length };
}


export function projectClipWordsToTimeline(
  words: readonly VoiceWord[],
  cue: { startSec: number; endSec?: number; mediaStartSec?: number; playbackRate?: number },
): VoiceWord[] {
  const base = Math.max(0, Number(cue.startSec) || 0);
  const media = Math.max(0, Number(cue.mediaStartSec) || 0);
  const rate = Number(cue.playbackRate) > 0 ? Number(cue.playbackRate) : 1;
  const winEnd = typeof cue.endSec === "number" && Number.isFinite(cue.endSec) && cue.endSec > base ? cue.endSec : Number.POSITIVE_INFINITY;
  const out: VoiceWord[] = [];
  for (const w of words) {
    const s0 = Number(w.start) || 0;
    const s1 = Math.max(s0, Number(w.end) || s0);
    if (s1 <= media) continue; // 머리 트림에 통째로 잘린 말
    const start = base + (Math.max(s0, media) - media) / rate;
    if (start >= winEnd) continue; // 씬 끝 뒤 — 화면에서 안 들린다
    const end = Math.min(winEnd, base + (s1 - media) / rate);
    out.push({ text: w.text, start, end: Math.max(start, end) });
  }
  return out;
}


export function alignLineTimingsFromWords(lines: string[], words: VoiceWord[]): LineTimingAlignment {
  const norm = normalizeForLineTiming;
  // ★꼬리 포기 폴백 제거(회장님 "리싱크를 눌러도 중반 이후가 그대로" 2026-07-27)★ — 종전에는 한 줄이
  //   매칭에 실패하면 그 줄부터 마지막 줄까지 전부 균등 분배하고 즉시 return 했다. 통짜 TTS 처럼 줄이
  //   많은 대본에서 한 줄만 어긋나도 후반부 전체가 추정치가 되고, 결정적 알고리즘이라 다시 눌러도
  //   같은 결과 → "버튼이 무효"로 체감된다. 이제 실패는 ★그 줄만 보류★하고 다음 줄로 진행하며,
  //   보류 줄은 루프가 끝난 뒤 이웃 실측 사이 선형 보간으로 채운다(전 줄 실패만 균등 분배 안전망).
  const measured: Array<VoiceLineTiming | null> = new Array(lines.length).fill(null);
  let wi = 0;
  let prevEnd = 0;
  // 🔭 직전 줄이 보류됐으면 그 줄이 먹었어야 할 단어들이 커서 앞에 그대로 남아 있다. 기본 앵커 창
  //   (±8)만으론 못 넘어가 뒷줄까지 연쇄 실패한다 → 실패 줄의 need(글자수)만큼 전방 스캔을 넓힌다.
  //   확장 창은 오매칭 대가가 크므로 일치 조건을 2자 → 3자로 강화(반복 대사 오매칭 방지).
  let extraForward = 0;
  const totalSec = words.length ? Math.max(0, Number(words[words.length - 1].end) || 0) : 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const need = Math.max(1, norm(line).length);
    const entryWi = wi; // 앵커 스냅 이전 커서 — 보류 시 여기까지 되돌려 단어를 잃지 않는다.
    // ★줄 시작 앵커 재보정(회장님 "중간부터 싱크 살짝씩 밀림" 2026-07-08) — 글자수 순차 소비는
    //   한 줄에서의 ASR 누락/과잉이 커서(wi)를 통해 뒤 줄 전체로 전파·누적된다(중간부터 점점
    //   어긋나는 원인). 각 줄 시작에서 줄의 첫 글자들을 커서 주변 단어와 대조해 일치 지점으로
    //   커서를 스냅 — 오차를 줄 단위로 리셋. 가까운 순 탐색+2자 이상 일치만 인정(반복 대사 오매칭
    //   방지·절반 폴백(아래)은 그대로 최후 안전망).
    const prefix = norm(line).slice(0, 6);
    let anchored = false;
    if (prefix.length >= 2) {
      const offsets = [0, 1, -1, 2, -2, 3, -3, 4, -4, 5, 6, 7, 8];
      for (let d = 9; d <= 8 + extraForward; d++) offsets.push(d); // 보류 직후에만 확장(평시 동작 불변)
      for (const d of offsets) {
        const k = wi + d;
        if (k < 0 || k >= words.length) continue;
        const w = norm(words[k].text);
        if (w.length < 2 || !(prefix.startsWith(w) || w.startsWith(prefix))) continue;
        if (d > 8 && Math.min(w.length, prefix.length) < 3) continue; // 확장 창은 3자 이상만 인정
        wi = k;
        anchored = true;
        break;
      }
    }
    const startSec = wi < words.length ? Math.max(prevEnd, Number(words[wi].start) || prevEnd) : prevEnd;
    let consumed = 0;
    let endSec = startSec;
    const startWi = wi;
    while (wi < words.length && consumed < need) {
      consumed += norm(words[wi].text).length;
      endSec = Math.max(endSec, Number(words[wi].end) || endSec);
      wi++;
    }
    // ★와카젠 mapSceneTimings 동급 fallback — 한 줄에서 필요 글자의 절반도 못 모으면
    //   (Soniox 가 단어를 놓쳤거나 TTS 가 글자를 씹은 것) ★그 줄만★ 실측을 포기하고 보류한다.
    //   커서를 원복(앵커 스냅 전 위치까지)해 이후 줄이 그 단어들을 다시 볼 수 있게 한다 —
    //   다음 줄의 prefix 앵커 스냅이 커서를 복구할 기회를 얻는 게 이 수리의 핵심.
    // ★복구 모드(직전 줄이 보류됨)에서는 앵커로 확인된 줄만 실측으로 확정한다(Codex P1)★ —
    //   보류로 커서를 원복하면 그 자리엔 그 줄과 무관한 잉여 토큰이 남아 있다. "왜?"·"헐!" 같이
    //   짧은 줄(prefix 2자 미만이라 앵커 자체가 불가)은 그 잉여를 그냥 삼켜 절반 조건을 통과해
    //   버리고, 엉뚱한 시각이 실측으로 굳으며 extraForward 까지 리셋된다. 앵커가 없으면 함께 보류.
    if (consumed < need * 0.5 || (extraForward > 0 && !anchored)) {
      wi = Math.min(entryWi, startWi);
      extraForward = Math.min(40, extraForward + need);
      continue; // measured[i] = null 유지 → 루프 뒤 이웃 실측 사이 선형 보간
    }
    extraForward = 0;
    prevEnd = endSec;
    measured[i] = { text: line, startSec, endSec };
  }
  // 하나도 못 맞췄으면(전 줄 보류) 종전 그대로 전체 균등 분배 — 최후 안전망.
  //   ★measured: 0 을 함께 낸다★(#1972) — 이 배열은 실측이 아니라 순수 추정이고, 그 사실을
  //   모르는 호출부가 그대로 적용하면 유저의 기존 타임라인을 추정치로 덮어쓴다(무고지 파괴).
  if (measured.every((m) => m === null)) {
    const per = Math.max(0.1, totalSec) / Math.max(1, lines.length);
    return {
      timings: lines.map((text, j) => ({ text, startSec: per * j, endSec: per * (j + 1) })),
      measured: 0,
      total: lines.length,
    };
  }
  // 보류 구간(연속 null)을 이웃 실측 사이에 선형 보간 — 앞 실측 endSec ↔ 뒤 실측 startSec 균등.
  //   앞이 없으면 0, 뒤가 없으면 음성 전체 길이(totalSec)를 경계로 쓴다.
  const out: VoiceLineTiming[] = [];
  for (let i = 0; i < lines.length; i++) {
    const hit = measured[i];
    if (hit) { out.push(hit); continue; }
    let end = i;
    while (end + 1 < lines.length && measured[end + 1] === null) end++;
    const left = i > 0 ? measured[i - 1]!.endSec : 0;
    const rightRaw = end + 1 < lines.length ? measured[end + 1]!.startSec : totalSec;
    const right = Math.max(left, rightRaw);
    const per = (right - left) / (end - i + 1);
    for (let j = i; j <= end; j++) {
      out.push({ text: lines[j], startSec: left + per * (j - i), endSec: left + per * (j - i + 1) });
    }
    i = end;
  }
  return { timings: out, measured: measured.reduce<number>((n, m) => n + (m ? 1 : 0), 0), total: lines.length };
}
